import React, {useMemo} from 'react';
import {EdgeAddChange, EdgeRemoveChange} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readQuerySafe, writeFragmentsToCache, writeQuery} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {NodeDetailsSidepanelContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import {createTaskNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import CreateProcessTaskDefinitionEditor from 'processes/settings/CreateProcessTaskDefinitionEditor';
import APIUtil from 'util/APIUtil';

import {ProcessTaskDefinitionGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {AllConfigurableTaskDefinitionsQuery} from '../__generated/AllConfigurableTaskDefinitionsQuery';
import {buildControlFlowEdge} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {FlowchartEdge, ProcessFlowchartEdgeType} from '../processFlowchartConfigEditorConstants';
import {PlaceholderNode} from '../processFlowchartConfigEditorNodeTypes';
import {createStaticNodeDataFromTaskDefinitionVersion} from '../processFlowchartConfigEditorUtils';
import {ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY} from '../useAllSchemasById';

export function cacheCreatedTaskDefinition(newTaskDefinition: ProcessTaskDefinitionGraphQL): void {
  const taskDefinitionsResult = readQuerySafe<AllConfigurableTaskDefinitionsQuery>({
    query: ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY,
  });
  if (taskDefinitionsResult != null) {
    const taskDefinitionsWithUpdate = [
      ...taskDefinitionsResult.processTaskDefinitionsSupportingPlatformFeature,
      newTaskDefinition,
    ];
    writeQuery(
      {query: ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY},
      {
        ...taskDefinitionsResult,
        processTaskDefinitionsSupportingPlatformFeature: taskDefinitionsWithUpdate,
        __typename: 'Query',
      }
    );
  }

  writeFragmentsToCache(getFragmentWrapper('ProcessTaskDefinition'), [newTaskDefinition]);
}

export default function PlaceholderNodeDetailsSidepanelContent(props: {
  selectedNode: PlaceholderNode;
}): JSX.Element {
  const {
    flowchartTaskSchema,
    onChangeFlowchart,
    interactiveFlowchartOptions,
    allSchemasById,
    interactiveFlowchart,
  } = NodeDetailsSidepanelContext.useContext();
  const allTaskSchemas = useMemo(
    () =>
      Object.values(allSchemasById).filter(
        (schema): schema is ProcessTaskSchemaGraphQL =>
          schema != null && APIUtil.isProcessTaskSchema(schema.id)
      ),
    [allSchemasById]
  );
  return (
    <CreateProcessTaskDefinitionEditor
      restrictedToExecutionType={props.selectedNode.data.placeholderForNodeTypeData.executionType}
      restrictedToOrganizationId={flowchartTaskSchema.organization.id}
      onCreateProcessTaskDefinition={(newTaskDefinition) => {
        assert(
          newTaskDefinition.latestVersion != null,
          'Creating a new task definition is expected to generate a version'
        );
        cacheCreatedTaskDefinition(newTaskDefinition);
        const newTaskSchema = assertIsAccessible(newTaskDefinition.latestVersion.processTaskSchema);
        const newId = uuidv4();
        const staticNodeData = createStaticNodeDataFromTaskDefinitionVersion({
          nodeClientId: newId,
          taskDefinitionVersion: newTaskDefinition.latestVersion,
          nodeDetailsType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
          allTaskSchemas: [...allTaskSchemas, newTaskSchema],
        });
        const taskNode = createTaskNode(
          staticNodeData,
          props.selectedNode.position,
          interactiveFlowchartOptions
        );

        // We need to replace any edges that are connected to the placeholder node with edges to the new
        // node.
        const edgeAddChanges: Array<EdgeAddChange<FlowchartEdge>> = [];
        const edgeRemoveChanges: Array<EdgeRemoveChange> = [];
        for (const edge of interactiveFlowchart.edges) {
          if (edge.source !== props.selectedNode.id && edge.target !== props.selectedNode.id) {
            continue;
          }
          edgeRemoveChanges.push({
            type: 'remove',
            id: edge.id,
          });
          assert(
            edge.type !== ProcessFlowchartEdgeType.DATA_FLOW,
            'Data flow edges should not be connected to the placeholder node'
          );
          const newSource = edge.source === props.selectedNode.id ? newId : edge.source;
          const newTarget = edge.target === props.selectedNode.id ? newId : edge.target;
          const staticEdge = buildControlFlowEdge({
            sourceNodeId: newSource,
            targetNodeId: newTarget,
          });
          edgeAddChanges.push({
            type: 'add',
            item: staticEdge,
          });
        }
        // We replace the placeholder node with the task node
        onChangeFlowchart({
          nodeChanges: [
            {
              type: 'remove',
              id: props.selectedNode.id,
            },
            {
              type: 'add',
              item: taskNode,
            },
          ],
          edgeChanges: [...edgeRemoveChanges, ...edgeAddChanges],
        });
      }}
    />
  );
}
